// HTTP Status Codes
const HTTP_STATUS = {
  BAD_REQUEST: 400,
  UNAUTHORIZED: 401,
  FORBIDDEN: 403,
  NOT_FOUND: 404,
  VALIDATION_ERROR: 422,
  INTERNAL_SERVER_ERROR: 500,
  SERVICE_UNAVAILABLE: 503,
};

export const ERROR_MESSAGES = {
  NETWORK_ERROR: "Error de conexión con el servidor",
  SERVER_ERROR: "Error interno del servidor",
  NOT_FOUND: "Recurso no encontrado",
  UNAUTHORIZED: "No autorizado. Por favor, inicie sesión nuevamente",
  FORBIDDEN: "No tiene permisos para realizar esta acción",
  VALIDATION_ERROR: "Error de validación en los datos",
  BAD_REQUEST: "Solicitud incorrecta",
  DEFAULT: "Ha ocurrido un error inesperado",
  SERVICE_UNAVAILABLE: "Servicio no disponible temporalmente",
  TIMEOUT: "La solicitud ha excedido el tiempo de espera",
};

export const handleApiError = (error) => {
  if (error.response) {
    // El servidor respondió con un estado fuera del rango 2xx
    const message = error.response.data.message || ERROR_MESSAGES.SERVER_ERROR;
    return new Error(message);
  } else if (error.request) {
    // La petición fue hecha pero no se recibió respuesta
    return new Error(ERROR_MESSAGES.NETWORK_ERROR);
  } else {
    // Algo sucedió al configurar la petición
    return new Error(ERROR_MESSAGES.DEFAULT);
  }
};

export const handleError = (error) => {
  // Si el error ya es una instancia de Error, lo retornamos
  if (error instanceof Error) {
    return error;
  }

  // Si es un error de API con respuesta
  if (error.response) {
    const { status } = error.response;
    switch (status) {
      case HTTP_STATUS.BAD_REQUEST:
        return new Error(
          error.response.data.message || ERROR_MESSAGES.BAD_REQUEST
        );
      case HTTP_STATUS.UNAUTHORIZED:
        return new Error(ERROR_MESSAGES.UNAUTHORIZED);
      case HTTP_STATUS.FORBIDDEN:
        return new Error(ERROR_MESSAGES.FORBIDDEN);
      case HTTP_STATUS.NOT_FOUND:
        return new Error(ERROR_MESSAGES.NOT_FOUND);
      case HTTP_STATUS.VALIDATION_ERROR:
        // Si hay errores de validación específicos, los concatenamos
        if (error.response.data.errors) {
          const errorMessages = Object.values(error.response.data.errors).join(
            ". "
          );
          return new Error(errorMessages);
        }
        return new Error(ERROR_MESSAGES.VALIDATION_ERROR);
      case HTTP_STATUS.SERVICE_UNAVAILABLE:
        return new Error(ERROR_MESSAGES.SERVICE_UNAVAILABLE);
      default:
        return new Error(
          error.response.data.message || ERROR_MESSAGES.SERVER_ERROR
        );
    }
  }

  // Si es un error de timeout
  if (error.code === "ECONNABORTED") {
    return new Error(ERROR_MESSAGES.TIMEOUT);
  }

  // Si es un error de red (sin respuesta del servidor)
  if (error.request) {
    return new Error(ERROR_MESSAGES.NETWORK_ERROR);
  }

  // Para cualquier otro tipo de error
  return new Error(ERROR_MESSAGES.DEFAULT);
};

// Función auxiliar para determinar si un error requiere reintento
export const shouldRetryError = (error) => {
  if (error.response) {
    const { status } = error.response;
    // Reintentar para errores de servidor o de red, pero no para errores de cliente
    return status >= 500 || status === HTTP_STATUS.SERVICE_UNAVAILABLE;
  }
  // Reintentar para errores de red y timeout
  return !error.response || error.code === "ECONNABORTED";
};

// Función auxiliar para errores de validación
export const extractValidationErrors = (error) => {
  if (
    error.response &&
    error.response.status === HTTP_STATUS.VALIDATION_ERROR
  ) {
    return error.response.data.errors || {};
  }
  return {};
};
